import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './StaffAdditionsReport.css';

const StaffAdditionsReport = () => {
  const [staffAdditions, setStaffAdditions] = useState([]);
  const [filteredAdditions, setFilteredAdditions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  
  const [selectedEmployeeDetail, setSelectedEmployeeDetail] = useState(null);
  const [employeeConsumptionDetail, setEmployeeConsumptionDetail] = useState([]);

  const currentUser = useSelector((state) => state.AuthReducer?.user);
  
  // 🔥 CONSTANTE TVA 7%
  const TVA_RATE = 0.07;

  // 🔥 Fonction pour calculer le TTC à partir du HT
  const calculateTTC = (htPrice) => {
    if (!htPrice || isNaN(htPrice)) return 0;
    return htPrice * (1 + TVA_RATE);
  };

  // 🔥 Fonction pour calculer le TTC d'un item
  const calculateItemTTC = (item) => {
    if (!item || !item.price || !item.quantity) return 0;
    return calculateTTC(item.price * item.quantity);
  };

  // 🔥 Fonction pour calculer le total TTC d'une addition
  const calculateAdditionTTC = (addition) => {
    if (!addition || !addition.total) return 0;
    return calculateTTC(addition.total);
  };

  // 🔥 Fonction pour calculer le HT à partir du TTC (si nécessaire)
  const calculateHT = (ttcPrice) => {
    if (!ttcPrice || isNaN(ttcPrice)) return 0;
    return ttcPrice / (1 + TVA_RATE);
  };

  // 🔥 CORRIGÉ : Charger les additions du personnel
  const fetchStaffAdditions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      console.log("🔍 Chargement des additions personnel...");
      
      const response = await axios.get('/api/Bills/getStaffAdditions', {
        headers: { Authorised: token },
        params: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        }
      });

      if (response.data && response.data.success) {
        console.log("✅ Données staff chargées:", response.data.staffAdditions);
        
        const additions = response.data.staffAdditions || [];
        
        // 🔥 DEBUG : Afficher la structure des données
        additions.forEach((addition, index) => {
          console.log(`📋 Addition ${index + 1}:`, {
            billNumber: addition.billNumber,
            isStaffAddition: addition.isStaffAddition,
            discount: addition.discount,
            staffMember: addition.staffMember,
            total: addition.total,
            totalTTC: calculateAdditionTTC(addition), // 🔥 AJOUT
            items: addition.items
          });
        });
        
        setStaffAdditions(additions);
        setFilteredAdditions(additions);
        await fetchEmployeesList();
      } else {
        throw new Error('Format de réponse invalide');
      }
      
    } catch (error) {
      console.error('❌ Erreur chargement additions personnel:', error);
      setStaffAdditions([]);
      setFilteredAdditions([]);
    } finally {
      setLoading(false);
    }
  };

  // 🔥 CORRIGÉ : Charger la liste complète des employés
  const fetchEmployeesList = async () => {
    try {
      const token = localStorage.getItem("token");
      console.log("👥 Début chargement des employés...");
      
      if (!token) {
        console.error("❌ Aucun token d'authentification trouvé");
        setEmployees([]);
        return;
      }

      let employeesData = [];

      // OPTION 1: Route des employés (Employee model)
      try {
        console.log("🔄 Tentative route /api/employees...");
        const response = await axios.get('/api/employees/', {
          headers: { 
            Authorised: token,
            'Content-Type': 'application/json'
          }
        });
        
        console.log("📊 Réponse employés:", response.data);
        
        if (Array.isArray(response.data)) {
          employeesData = response.data;
        } else if (response.data && Array.isArray(response.data.employees)) {
          employeesData = response.data.employees;
        } else if (response.data && Array.isArray(response.data.data)) {
          employeesData = response.data.data;
        }
        
        if (employeesData.length > 0) {
          console.log(`🎉 ${employeesData.length} employés chargés depuis /api/employees`);
          setEmployees(employeesData);
          return;
        }
        
      } catch (mainError) {
        console.error("❌ Erreur route employés:", mainError);
      }

      // OPTION 2: Route d'authentification
      try {
        console.log("🔄 Tentative route /api/auth/GetEmployees...");
        const fallbackResponse = await axios.get('/api/auth/GetEmployees', {
          headers: { 
            Authorised: token,
            'Content-Type': 'application/json'
          }
        });
        
        if (fallbackResponse.data && fallbackResponse.data.success && Array.isArray(fallbackResponse.data.employees)) {
          console.log(`✅ ${fallbackResponse.data.employees.length} employés chargés via GetEmployees`);
          setEmployees(fallbackResponse.data.employees);
          return;
        }
      } catch (error2) {
        console.error("❌ Erreur GetEmployees:", error2);
      }

      // OPTION 3: Tous les utilisateurs
      try {
        console.log("🔄 Tentative route /api/auth/AllUser...");
        const fallbackResponse2 = await axios.get('/api/auth/AllUser', {
          headers: { 
            Authorised: token,
            'Content-Type': 'application/json'
          }
        });
        
        if (fallbackResponse2.data && fallbackResponse2.data.AllUser && Array.isArray(fallbackResponse2.data.AllUser)) {
          const staffUsers = fallbackResponse2.data.AllUser.filter(user => 
            (user.role === "user" || user.role === "userspintoss" || user.role === "employee") && 
            user.name
          );
          console.log(`✅ ${staffUsers.length} employés chargés via AllUser`);
          setEmployees(staffUsers);
          return;
        }
      } catch (error3) {
        console.error("❌ Erreur AllUser:", error3);
      }

      console.log("⚠️ Aucun employé chargé depuis les APIs");
      setEmployees([]);
      
    } catch (error) {
      console.error("❌ Erreur générale chargement employés:", error);
      setEmployees([]);
    }
  };

  // 🔥 CORRIGÉ : Obtenir les informations de l'employé depuis la facture
  const getEmployeeInfoFromBill = (bill) => {
    console.log("🔍 Analyse facture:", {
      billNumber: bill.billNumber,
      staffMember: bill.staffMember,
      isStaffAddition: bill.isStaffAddition,
      discount: bill.discount,
      totalHT: bill.total,
      totalTTC: calculateAdditionTTC(bill) // 🔥 AJOUT
    });

    // 🔥 CORRECTION : Si staffMember existe et est peuplé
    if (bill.staffMember && bill.staffMember._id) {
      console.log("✅ Employé trouvé dans staffMember:", bill.staffMember);
      return {
        _id: bill.staffMember._id,
        name: bill.staffMember.name || 'Employé inconnu',
        matricule: bill.staffMember.matricule || 'N/A'
      };
    }

    // 🔥 CORRECTION : Si staffMember est un ID string (non peuplé)
    if (bill.staffMember && typeof bill.staffMember === 'string') {
      console.log("🔍 staffMember est un ID string:", bill.staffMember);
      // Chercher dans la liste des employés
      const employee = employees.find(emp => emp._id === bill.staffMember);
      if (employee) {
        console.log("✅ Employé trouvé via ID:", employee);
        return {
          _id: employee._id,
          name: employee.name || 'Employé inconnu',
          matricule: employee.matricule || employee.employeeId || employee.code || 'N/A'
        };
      }
    }

    // 🔥 NOUVEAU : Si pas de staffMember mais c'est une addition staff
    if (bill.isStaffAddition === true || bill.discount === 20) {
      console.log("⚠️ Facture staff sans staffMember, utilisation par défaut");
      // Dans un système réel, vous devriez avoir une logique pour lier la facture à l'employé
      // Pour l'instant, retourner un employé générique
      return {
        _id: 'unknown-staff',
        name: 'Employé Staff',
        matricule: 'STAFF001'
      };
    }

    console.log("❌ Aucune information employé trouvée");
    return null;
  };

  // Voir le détail de consommation d'un employé
  const viewEmployeeDetail = (employee) => {
    setSelectedEmployeeDetail(employee);
    
    const employeeAdditions = staffAdditions.filter(addition => {
      const employeeInfo = getEmployeeInfoFromBill(addition);
      return employeeInfo && employeeInfo._id === employee._id;
    });
    
    setEmployeeConsumptionDetail(employeeAdditions);
  };

  // Fermer le détail
  const closeEmployeeDetail = () => {
    setSelectedEmployeeDetail(null);
    setEmployeeConsumptionDetail([]);
  };

  // 🔥 CORRIGÉ : Calculer le total de consommation TTC d'un employé
  const calculateEmployeeTotal = (employeeId) => {
    const employeeAdditions = staffAdditions.filter(addition => {
      const employeeInfo = getEmployeeInfoFromBill(addition);
      return employeeInfo && employeeInfo._id === employeeId;
    });
    
    return employeeAdditions.reduce((total, addition) => 
      total + calculateAdditionTTC(addition), 0
    ).toFixed(2);
  };

  // 🔥 CORRIGÉ : Calculer les articles les plus consommés par un employé (en TTC)
  const getEmployeeTopItems = (employeeId) => {
    const employeeAdditions = staffAdditions.filter(addition => {
      const employeeInfo = getEmployeeInfoFromBill(addition);
      return employeeInfo && employeeInfo._id === employeeId;
    });
    
    const itemMap = {};
    
    employeeAdditions.forEach(addition => {
      addition.items.forEach(item => {
        if (!itemMap[item.name]) {
          itemMap[item.name] = {
            name: item.name,
            quantity: 0,
            totalHT: 0,
            totalTTC: 0,
            count: 0
          };
        }
        itemMap[item.name].quantity += item.quantity;
        itemMap[item.name].totalHT += item.price * item.quantity;
        itemMap[item.name].totalTTC += calculateItemTTC(item); // 🔥 TTC
        itemMap[item.name].count += 1;
      });
    });
    
    return Object.values(itemMap).sort((a, b) => b.quantity - a.quantity);
  };

  // Charger les localisations
  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem("token");
      
      try {
        const response = await axios.get('/api/Locationspin/getAllLocations', {
          headers: { Authorised: token }
        });
        
        let locationsData = [];
        
        if (response.data && Array.isArray(response.data)) {
          locationsData = response.data;
        } else if (response.data && response.data.locations) {
          locationsData = response.data.locations;
        } else if (response.data && response.data.AllLocations) {
          locationsData = response.data.AllLocations;
        }
        
        setLocations(locationsData);
        
      } catch (error) {
        console.error("❌ Erreur chargement localisations:", error);
        const locationsFromData = getLocationsFromAdditions();
        setLocations(locationsFromData);
      }
      
    } catch (error) {
      console.error('❌ Erreur finale chargement localisations:', error);
      const locationsFromData = getLocationsFromAdditions();
      setLocations(locationsFromData);
    }
  };

  // Extraire les localisations depuis les additions
  const getLocationsFromAdditions = () => {
    const locationsFromData = new Map();
    
    staffAdditions.forEach(addition => {
      if (addition.localisation) {
        let locId, locName;
        
        if (typeof addition.localisation === 'string') {
          locId = addition.localisation;
          locName = `Localisation ${locId}`;
        } else if (addition.localisation._id) {
          locId = addition.localisation._id;
          locName = addition.localisation.name || `Localisation ${locId}`;
        }
        
        if (locId && !locationsFromData.has(locId)) {
          locationsFromData.set(locId, {
            _id: locId,
            name: locName
          });
        }
      }
    });
    
    const locationsArray = Array.from(locationsFromData.values());
    
    if (locationsArray.length === 0) {
      return [
        { _id: 'loc1', name: 'SPINTOSS Twin Park' },
        { _id: 'loc2', name: 'SPINTOSS Bagngo' },
        { _id: 'loc3', name: 'SPINTOSS Wellness' }
      ];
    }
    
    return locationsArray;
  };

  // Appliquer les filtres
  const applyFilters = () => {
    let filtered = staffAdditions;

    // Filtre par localisation
    if (selectedLocation !== 'all') {
      filtered = filtered.filter(addition => {
        let locId;
        if (typeof addition.localisation === 'string') {
          locId = addition.localisation;
        } else if (addition.localisation && addition.localisation._id) {
          locId = addition.localisation._id;
        }
        return locId === selectedLocation;
      });
    }

    // Filtre par employé
    if (selectedEmployee !== 'all') {
      filtered = filtered.filter(addition => {
        const employeeInfo = getEmployeeInfoFromBill(addition);
        return employeeInfo && employeeInfo._id === selectedEmployee;
      });
    }

    setFilteredAdditions(filtered);
  };

  // 🔥 CORRIGÉ : Calculer les totaux en TTC
  const calculateTotals = () => {
    return filteredAdditions.reduce((acc, addition) => 
      acc + calculateAdditionTTC(addition), 0
    ).toFixed(2);
  };

  // 🔥 CORRIGÉ : Calculer par employé (en TTC)
  const calculateByEmployee = () => {
    const employeeTotals = {};
    
    filteredAdditions.forEach(addition => {
      const employeeInfo = getEmployeeInfoFromBill(addition);
      
      if (employeeInfo) {
        const employeeId = employeeInfo._id;
        const additionTTC = calculateAdditionTTC(addition); // 🔥 TTC
        
        if (!employeeTotals[employeeId]) {
          employeeTotals[employeeId] = {
            id: employeeId,
            name: employeeInfo.name,
            matricule: employeeInfo.matricule,
            totalHT: 0,
            totalTTC: 0,
            count: 0,
            items: []
          };
        }
        
        employeeTotals[employeeId].totalHT += addition.total || 0;
        employeeTotals[employeeId].totalTTC += additionTTC; // 🔥 TTC
        employeeTotals[employeeId].count += 1;
        
        // Ajouter les articles
        addition.items.forEach(item => {
          employeeTotals[employeeId].items.push({
            name: item.name,
            quantity: item.quantity,
            priceHT: item.price,
            priceTTC: calculateTTC(item.price), // 🔥 TTC
            totalHT: item.price * item.quantity,
            totalTTC: calculateItemTTC(item), // 🔥 TTC
            date: addition.date,
            billNumber: addition.billNumber
          });
        });
      }
    });

    return Object.values(employeeTotals);
  };

  // 🔥 CORRIGÉ : Export Excel avec TTC
  const exportToExcel = () => {
    const employeeData = calculateByEmployee();
    
    const detailedData = filteredAdditions.map(addition => {
      const employeeInfo = getEmployeeInfoFromBill(addition);
      const totalTTC = calculateAdditionTTC(addition); // 🔥 TTC
      const totalHT = addition.total || 0;
      
      return {
        'Date': new Date(addition.date).toLocaleDateString(),
        'Employé': employeeInfo?.name || 'N/A',
        'Matricule': employeeInfo?.matricule || 'N/A',
        'Localisation': addition.localisation?.name || 'Non définie',
        'Facture': addition.billNumber,
        'Articles': addition.items.map(item => `${item.name} x${item.quantity}`).join(', '),
        'Quantité Total': addition.items.reduce((sum, item) => sum + item.quantity, 0),
        'Total HT (DT)': totalHT.toFixed(2),
        'Total TTC (DT)': totalTTC.toFixed(2), // 🔥 AJOUT
        'TVA (7%)': (totalTTC - totalHT).toFixed(2), // 🔥 AJOUT
        'Réduction': `${addition.discount || 0}%`,
        'Paiement': addition.paymentMethod
      };
    });

    const summaryData = employeeData.map(employee => ({
      'Employé': employee.name,
      'Matricule': employee.matricule,
      'Nombre d\'Additions': employee.count,
      'Total HT (DT)': employee.totalHT.toFixed(2),
      'Total TTC (DT)': employee.totalTTC.toFixed(2), // 🔥 AJOUT
      'TVA (7%)': (employee.totalTTC - employee.totalHT).toFixed(2), // 🔥 AJOUT
      'Moyenne TTC par Addition': employee.count > 0 ? (employee.totalTTC / employee.count).toFixed(2) : '0.00'
    }));

    const wb = XLSX.utils.book_new();
    
    const ws1 = XLSX.utils.json_to_sheet(detailedData);
    XLSX.utils.book_append_sheet(wb, ws1, 'Détail des Consommations');
    
    const ws2 = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, ws2, 'Résumé par Employé');

    XLSX.writeFile(wb, `consommations-personnel-${dateRange.startDate}-${dateRange.endDate}.xlsx`);
  };

  // 🔥 CORRIGÉ : Export PDF (en TTC)
  const exportToPDF = () => {
    const doc = new jsPDF();
    const employeeData = calculateByEmployee();
    
    doc.setFontSize(16);
    doc.text('RAPPORT DES CONSOMMATIONS PERSONNEL', 20, 20);
    doc.setFontSize(12);
    doc.text(`Période: ${dateRange.startDate} à ${dateRange.endDate}`, 20, 30);
    doc.text(`Total TTC: ${calculateTotals()} DT - ${filteredAdditions.length} additions`, 20, 40);
    doc.text(`TVA: 7%`, 20, 50);

    let yPosition = 70;

    if (employeeData.length > 0) {
      doc.setFontSize(14);
      doc.text('RÉSUMÉ PAR EMPLOYÉ (TTC)', 20, yPosition);
      yPosition += 10;

      employeeData.forEach((employee, index) => {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(`${employee.name} (${employee.matricule})`, 20, yPosition);
        doc.setFont(undefined, 'normal');
        
        yPosition += 7;
        doc.text(`Additions: ${employee.count}`, 25, yPosition);
        yPosition += 7;
        doc.text(`Total TTC: ${employee.totalTTC.toFixed(2)} DT`, 25, yPosition);
        yPosition += 7;
        doc.text(`Total HT: ${employee.totalHT.toFixed(2)} DT`, 25, yPosition);
        yPosition += 7;
        doc.text(`TVA: ${(employee.totalTTC - employee.totalHT).toFixed(2)} DT`, 25, yPosition);
        yPosition += 7;
        doc.text(`Moyenne TTC: ${(employee.totalTTC / employee.count).toFixed(2)} DT`, 25, yPosition);
        yPosition += 12;
      });
    } else {
      doc.text('Aucune donnée disponible', 20, yPosition);
    }

    doc.save(`consommations-personnel-${dateRange.startDate}-${dateRange.endDate}.pdf`);
  };

  // 🔥 CORRIGÉ : Export PDF détaillé (en TTC)
  const exportDetailedPDF = () => {
    const doc = new jsPDF();
    
    doc.setFontSize(16);
    doc.text('DÉTAIL DES CONSOMMATIONS PERSONNEL (TTC)', 20, 20);
    doc.setFontSize(12);
    doc.text(`Période: ${dateRange.startDate} à ${dateRange.endDate}`, 20, 30);
    doc.text(`TVA: 7%`, 20, 40);

    const tableData = filteredAdditions.map(addition => {
      const employeeInfo = getEmployeeInfoFromBill(addition);
      const totalTTC = calculateAdditionTTC(addition); // 🔥 TTC
      
      return [
        new Date(addition.date).toLocaleDateString(),
        employeeInfo?.name || 'N/A',
        employeeInfo?.matricule || 'N/A',
        addition.billNumber,
        addition.items.map(item => `${item.name} x${item.quantity}`).join('\n'),
        addition.items.reduce((sum, item) => sum + item.quantity, 0),
        `${totalTTC.toFixed(2)} DT`, // 🔥 TTC
        `${(totalTTC - (addition.total || 0)).toFixed(2)} DT`, // TVA
        `${addition.discount}%`
      ];
    });

    if (tableData.length > 0) {
      doc.autoTable({
        startY: 50,
        head: [['Date', 'Employé', 'Matricule', 'Facture', 'Articles', 'Quantité', 'Total TTC', 'TVA (7%)', 'Réduction']],
        body: tableData,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [41, 128, 185] }
      });
    } else {
      doc.text('Aucune donnée disponible', 20, 50);
    }

    doc.save(`detail-consommations-${dateRange.startDate}-${dateRange.endDate}.pdf`);
  };

  useEffect(() => {
    fetchLocations();
    fetchStaffAdditions();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [selectedLocation, selectedEmployee, staffAdditions]);

  useEffect(() => {
    fetchStaffAdditions();
  }, [dateRange]);

  // Obtenir la liste des employés disponibles (sans filtre pour admin)
  const availableEmployees = currentUser?.role === 'admin' 
    ? employees 
    : employees.filter(emp => {
        if (!currentUser?.localisation) return true;
        if (!emp.localisation) return true;
        
        const userLocId = currentUser.localisation._id || currentUser.localisation;
        const empLocId = emp.localisation._id || emp.localisation;
        return userLocId === empLocId;
      });

  if (!currentUser || (currentUser.role !== 'admin' && currentUser.role !== 'userspintoss')) {
    return (
      <div className="access-denied">
        <h2>⛔ Accès Refusé</h2>
        <p>Vous n'avez pas les permissions nécessaires pour accéder à cette page.</p>
      </div>
    );
  }

  return (
    <div className="staff-additions-report">
      <div className="report-header">
        <div className="header-top">
          <h1>📊 Rapport des Consommations Personnel</h1>
          <div className="info-tva">
            <span className="tva-badge">TVA: 7%</span>
          </div>
          <div className="export-buttons">
            <button onClick={exportToExcel} className="export-btn excel">
              📊 Excel
            </button>
            <button onClick={exportDetailedPDF} className="export-btn pdf">
              📋 PDF Détaillé
            </button>
          </div>
        </div>
        <p>Consommation du personnel par localisation, employé et période (TTC)</p>
      </div>

      {/* Filtres */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Date de début:</label>
          <input
            type="date"
            value={dateRange.startDate}
            onChange={(e) => setDateRange(prev => ({
              ...prev,
              startDate: e.target.value
            }))}
          />
        </div>

        <div className="filter-group">
          <label>Date de fin:</label>
          <input
            type="date"
            value={dateRange.endDate}
            onChange={(e) => setDateRange(prev => ({
              ...prev,
              endDate: e.target.value
            }))}
          />
        </div>

        <div className="filter-group">
          <label>Localisation:</label>
          <select
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
          >
            <option value="all">Toutes les localisations</option>
            {locations.map(location => (
              <option key={location._id} value={location._id}>
                {location.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Employé:</label>
          <select
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
          >
            <option value="all">Tous les employés</option>
            {availableEmployees.map(employee => (
              <option key={employee._id} value={employee._id}>
                {employee.name} ({employee.matricule || employee.employeeId || employee.code || 'N/A'})
              </option>
            ))}
          </select>
          
          <div style={{marginTop: '5px', fontSize: '11px', color: '#666'}}>
            {availableEmployees.length === 0 ? (
              <span style={{color: '#f44336'}}>⚠️ Aucun employé trouvé</span>
            ) : (
              <span style={{color: '#4caf50'}}>✅ {availableEmployees.length} employé(s) disponible(s)</span>
            )}
          </div>
        </div>

        <button 
          onClick={fetchStaffAdditions}
          disabled={loading}
          className="refresh-btn"
        >
          {loading ? '🔄 Chargement...' : '🔄 Actualiser'}
        </button>
      </div>

      {/* 🔥 CORRIGÉ : Statistiques résumées en TTC */}
      <div className="summary-stats">
        <div className="stat-card">
          <h3>Total TTC</h3>
          <p className="stat-value">{calculateTotals()} DT</p>
          <p className="stat-subtext">
            HT: {filteredAdditions.reduce((acc, addition) => 
              acc + (addition.total || 0), 0
            ).toFixed(2)} DT
          </p>
        </div>
        <div className="stat-card">
          <h3>Nombre d'Additions</h3>
          <p className="stat-value">{filteredAdditions.length}</p>
        </div>
        <div className="stat-card">
          <h3>Employés</h3>
          <p className="stat-value">{calculateByEmployee().length}</p>
        </div>
        <div className="stat-card">
          <h3>TVA Totale (7%)</h3>
          <p className="stat-value tva">
            {(
              filteredAdditions.reduce((acc, addition) => 
                acc + (calculateAdditionTTC(addition) - (addition.total || 0)), 0
              )
            ).toFixed(2)} DT
          </p>
        </div>
      </div>

      {/* 🔥 CORRIGÉ : Détail par employé (TTC) */}
      {selectedEmployeeDetail && (
        <div className="employee-detail-modal">
          <div className="detail-content">
            <div className="detail-header">
              <h2>📋 Détail de consommation - {selectedEmployeeDetail.name}</h2>
              <div className="tva-info-header">TVA: 7%</div>
              <button onClick={closeEmployeeDetail} className="close-btn">✕</button>
            </div>
            
            <div className="employee-summary-info">
              <div className="summary-row">
                <span>Matricule:</span>
                <strong>{selectedEmployeeDetail.matricule}</strong>
              </div>
              <div className="summary-row">
                <span>Total TTC:</span>
                <strong className="total-amount">{calculateEmployeeTotal(selectedEmployeeDetail._id)} DT</strong>
              </div>
              <div className="summary-row">
                <span>Total HT:</span>
                <strong className="ht-amount">
                  {staffAdditions
                    .filter(addition => {
                      const employeeInfo = getEmployeeInfoFromBill(addition);
                      return employeeInfo && employeeInfo._id === selectedEmployeeDetail._id;
                    })
                    .reduce((total, addition) => total + (addition.total || 0), 0)
                    .toFixed(2)} DT
                </strong>
              </div>
              <div className="summary-row">
                <span>TVA (7%):</span>
                <strong className="tva-amount">
                  {(
                    parseFloat(calculateEmployeeTotal(selectedEmployeeDetail._id)) - 
                    staffAdditions
                      .filter(addition => {
                        const employeeInfo = getEmployeeInfoFromBill(addition);
                        return employeeInfo && employeeInfo._id === selectedEmployeeDetail._id;
                      })
                      .reduce((total, addition) => total + (addition.total || 0), 0)
                  ).toFixed(2)} DT
                </strong>
              </div>
              <div className="summary-row">
                <span>Nombre d'additions:</span>
                <strong>{employeeConsumptionDetail.length}</strong>
              </div>
            </div>

            <div className="top-items-section">
              <h3>🛒 Articles les plus consommés (TTC)</h3>
              <div className="top-items-list">
                {getEmployeeTopItems(selectedEmployeeDetail._id).slice(0, 5).map((item, index) => (
                  <div key={index} className="top-item">
                    <span className="item-name">{item.name}</span>
                    <span className="item-quantity">x{item.quantity}</span>
                    <span className="item-total">{item.totalTTC.toFixed(2)} DT</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="detail-additions">
              <h3>📝 Détail des additions (TTC)</h3>
              {employeeConsumptionDetail.length === 0 ? (
                <p className="no-data">Aucune addition trouvée</p>
              ) : (
                <table className="detail-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Facture</th>
                      <th>Localisation</th>
                      <th>Articles</th>
                      <th>Quantité</th>
                      <th>Total TTC</th>
                      <th>HT</th>
                      <th>TVA</th>
                      <th>Paiement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employeeConsumptionDetail.map((addition, index) => {
                      const additionTTC = calculateAdditionTTC(addition);
                      const additionHT = addition.total || 0;
                      const tvaAmount = additionTTC - additionHT;
                      
                      return (
                        <tr key={addition._id || index}>
                          <td>{new Date(addition.date).toLocaleDateString()}</td>
                          <td className="center">#{addition.billNumber}</td>
                          <td>{addition.localisation?.name || 'Non définie'}</td>
                          <td>
                            <div className="items-list">
                              {addition.items.map((item, idx) => (
                                <div key={idx} className="item-line">
                                  {item.name} x{item.quantity}
                                </div>
                              ))}
                            </div>
                          </td>
                          <td className="center">
                            {addition.items.reduce((sum, item) => sum + item.quantity, 0)}
                          </td>
                          <td className="amount-cell ttc-cell">{additionTTC.toFixed(2)} DT</td>
                          <td className="amount-cell ht-cell">{additionHT.toFixed(2)} DT</td>
                          <td className="amount-cell tva-cell">{tvaAmount.toFixed(2)} DT</td>
                          <td>{addition.paymentMethod}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 🔥 CORRIGÉ : Résumé par employé (TTC) */}
      <div className="employee-summary">
        <h2>Résumé par Employé (TTC)</h2>
        {calculateByEmployee().length === 0 ? (
          <div className="no-data">
            {staffAdditions.length === 0 
              ? 'Aucune donnée disponible pour la période sélectionnée' 
              : 'Aucune information employé trouvée dans les additions'}
          </div>
        ) : (
          <table className="employee-table">
            <thead>
              <tr>
                <th>Employé</th>
                <th>Matricule</th>
                <th>Additions</th>
                <th>Total HT (DT)</th>
                <th>Total TTC (DT)</th>
                <th>TVA (7%)</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {calculateByEmployee().map((employee) => {
                const tvaAmount = employee.totalTTC - employee.totalHT;
                
                return (
                  <tr key={employee.id}>
                    <td>
                      <strong>{employee.name}</strong>
                    </td>
                    <td>{employee.matricule}</td>
                    <td className="center">{employee.count}</td>
                    <td className="amount-cell ht-cell">{employee.totalHT.toFixed(2)}</td>
                    <td className="amount-cell ttc-cell">{employee.totalTTC.toFixed(2)}</td>
                    <td className="amount-cell tva-cell">{tvaAmount.toFixed(2)}</td>
                    <td className="actions-cell">
                      <button 
                        onClick={() => viewEmployeeDetail(employee)}
                        className="view-detail-btn"
                      >
                        👁️ Voir détail
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* 🔥 CORRIGÉ : Tableau détaillé (TTC) */}
      <div className="additions-table-container">
        <h2>Détail des Consommations TTC ({filteredAdditions.length})</h2>
        
        {loading ? (
          <div className="loading">Chargement des données...</div>
        ) : filteredAdditions.length === 0 ? (
          <div className="no-data">Aucune consommation trouvée pour les critères sélectionnés</div>
        ) : (
          <table className="staff-additions-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Employé</th>
                <th>Matricule</th>
                <th>Localisation</th>
                <th>Facture</th>
                <th>Articles</th>
                <th>Quantité</th>
                <th>Total HT (DT)</th>
                <th>Total TTC (DT)</th>
                <th>TVA (7%)</th>
                <th>Réduction</th>
                <th>Paiement</th>
              </tr>
            </thead>
            <tbody>
              {filteredAdditions.map((addition) => {
                const itemsQuantity = addition.items.reduce((sum, item) => sum + item.quantity, 0);
                const employeeInfo = getEmployeeInfoFromBill(addition);
                const totalTTC = calculateAdditionTTC(addition);
                const totalHT = addition.total || 0;
                const tvaAmount = totalTTC - totalHT;
                
                return (
                  <tr key={addition._id}>
                    <td>{new Date(addition.date).toLocaleDateString()}</td>
                    <td>
                      <strong>{employeeInfo?.name || 'N/A'}</strong>
                    </td>
                    <td>{employeeInfo?.matricule || 'N/A'}</td>
                    <td>{addition.localisation?.name || 'Non définie'}</td>
                    <td className="center">#{addition.billNumber}</td>
                    <td>
                      <div className="items-list">
                        {addition.items.map((item, idx) => (
                          <div key={idx} className="item-line">
                            {item.name} x{item.quantity}
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="center">{itemsQuantity}</td>
                    <td className="amount-cell ht-cell">{totalHT.toFixed(2)}</td>
                    <td className="amount-cell ttc-cell">{totalTTC.toFixed(2)}</td>
                    <td className="amount-cell tva-cell">{tvaAmount.toFixed(2)}</td>
                    <td className="center">{addition.discount}%</td>
                    <td>{addition.paymentMethod}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default StaffAdditionsReport;